import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { get as getRequest, remove as removeRequest, update as updateRequest } from './requests';
import { toast } from 'react-toastify';
import { CreatePromptRequest, Prompt, QueryKey } from 'api/model';
import { errorHandler } from 'api/axios_instance';

const queryKey = QueryKey.Prompts;

type Props = {
  promptId: string;
  revision?: number;
  prompt?: Prompt;
};

const usePrompt = ({ promptId, revision, prompt: initialPrompt }: Props) => {
  const queryClient = useQueryClient();

  const {
    data: latestPrompt,
    isLoading: promptIsLoading,
    isFetched: promptIsFetched,
  } = useQuery({
    queryKey: [queryKey, { id: promptId, revision }],
    queryFn: () => getRequest(promptId, revision),
    enabled: !!promptId && !initialPrompt,
  });

  const prompt = latestPrompt ?? initialPrompt;

  const update = useMutation({
    mutationFn: (prompt: CreatePromptRequest) => updateRequest(promptId, prompt),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [queryKey] });
      void queryClient.invalidateQueries({ queryKey: [QueryKey.PromptTestCases] });
      void queryClient.invalidateQueries({ queryKey: [QueryKey.PromptFolders] });
      toast.success('Prompts successfully updated');
    },
    onError: errorHandler('Updating prompt failed'),
  });

  const remove = useMutation({
    mutationFn: () => removeRequest(promptId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [queryKey] });
      void queryClient.invalidateQueries({ queryKey: [QueryKey.PromptFolders] });
      toast.success('Prompt successfully removed');
    },
    onError: errorHandler('Removing prompt failed'),
  });

  return { prompt, promptIsLoading, promptIsFetched, remove, update };
};

export default usePrompt;
